import { useCallback, useEffect, useMemo, useState } from 'react'

import { AssignmentOutlined, Close, Restore } from '@mui/icons-material'
import {
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridColDef,
  GridColumnVisibilityModel,
  GridEventListener,
  GridFilterItem,
  GridFilterModel,
  GridFilterOperator,
  GridLocaleText,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
  GridValueFormatterParams,
  GridValueGetterParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro'

import { useDispatch } from '@tabeeb/store'
import {
  Chip,
  DataGridV2,
  Grid,
  UserInfo,
  useDebouncedValue,
  useDialogState,
  usePagination,
} from '@collaborate-health/uikit'

import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'
import { UserQualificationsDisplayType } from '@tabeeb/modules/qualifications/constants'
import type { UserQualificationFilterModelDto } from '@tabeeb/contracts/microservices/swagger-certificate-service'
import {
  GetUserQualificationSortColumn,
  GetUserQualificationFilterColumn,
  GetUserQualificationFilterOperator,
  SortingOrder,
} from '@tabeeb/contracts/microservices/swagger-certificate-service'
import { UserResponse } from '@tabeeb/contracts/main-api/swagger-main-api'

import {
  CertificateTypeGridFilterOperator,
  UserGridFilterOperator,
} from '@tabeeb/modules/certificates/components/Filters'
import { CertificateType } from '@tabeeb/modules/certificates/types'

import { useUserQualifications } from '../../hooks'
import { UserQualification } from '../../types'
import { Scope, UserAssignedQualificationStatusType, UserAssignedQualificationStatusTypeDisplayName } from '../../enums'

import AddUserQualificationDialog, { AddUserQualificationDialogProps } from '../AddUserQualificationDialog'
import ViewUserQualificationDialog from '../ViewUserQualificationDialog'
import UserQualificationStatusChip from '../UserQualificationStatusChip'
import UserQualificationsGridToolbar, { UserQualificationsGridToolbarProps } from './UserQualificationsGridToolbar'

const checkBoxFilterOperators = getGridSingleSelectOperators().filter(({ value }) => value === 'isAnyOf')
const assignedToFilterOperator = getGridStringOperators()
  .filter(({ value }) => value === 'contains')
  .map((operator) => ({ ...operator, InputComponent: UserGridFilterOperator }))

const assignerFilterOperator = getGridStringOperators()
  .filter(({ value }) => value === 'contains')
  .map((operator) => ({ ...operator, InputComponent: UserGridFilterOperator }))

const filterValueFormatter = (filter: GridFilterItem) => {
  if (filter.field === 'QualificationVersionCertificateTypes') {
    return JSON.stringify(filter.value.map((_: CertificateType) => _.Id))
  }
  if (filter.field === 'AssignerId' || filter.field === 'AssignedToUserId') {
    return JSON.stringify(filter.value.map((_: UserResponse) => _.IdentityGuid))
  }
  return JSON.stringify(filter.value)
}

export interface UserQualificationsGridProps {
  scope?: Scope
  personal?: boolean
  simpleView?: boolean
  user?: {
    Id: number
    IdentityGuid: string
  } | null
  contractVersion?: {
    Id: number
  }
  displayType?: typeof UserQualificationsDisplayType[keyof typeof UserQualificationsDisplayType]
  toolbar?: React.FC<UserQualificationsGridToolbarProps> | null
}

const UserQualificationsGrid: React.FC<UserQualificationsGridProps> = ({
  scope: initialScope = Scope.Tenant,
  personal = false,
  simpleView = false,
  user,
  contractVersion,
  displayType = UserQualificationsDisplayType.All,
  toolbar: Toolbar = UserQualificationsGridToolbar,
}) => {
  const dispatch = useDispatch()
  const gridApiRef = useGridApiRef()

  const [scope, setScope] = useState<Scope>(initialScope)
  const [totalCount, setTotalCount] = useState(0)
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>()
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>()
  const [search, setSearch] = useState<string>('')
  const debouncedSearch = useDebouncedValue(search, 500)

  const sorting = useMemo<{ field: GetUserQualificationSortColumn; order: SortingOrder } | null>(() => {
    if (!sortModel || sortModel.length === 0) {
      return null
    }

    const sortItem = sortModel[0]
    const fieldReverseMap: Record<string, GetUserQualificationSortColumn> = {
      QualificationVersionExpirationDate: GetUserQualificationSortColumn.QualificationVersionExpirationDate,
    }

    if (!fieldReverseMap[sortItem.field] || !sortItem.sort) {
      return null
    }

    return {
      field: fieldReverseMap[sortItem.field],
      order: sortItem.sort === 'asc' ? SortingOrder.Asc : SortingOrder.Desc,
    }
  }, [sortModel])

  const filtering = useMemo<UserQualificationFilterModelDto[]>(() => {
    const fieldReverseMap: Record<string, GetUserQualificationFilterColumn> = {
      StatusId: GetUserQualificationFilterColumn.StatusId,
      AssignedToUserId: GetUserQualificationFilterColumn.AssignedToUserId,
      AssignerId: GetUserQualificationFilterColumn.AssignerId,
      QualificationVersionCertificateTypes: GetUserQualificationFilterColumn.QualificationVersionCertificateTypes,
    }

    const operatorReverseMap: Record<string, GetUserQualificationFilterOperator> = {
      isAnyOf: GetUserQualificationFilterOperator.IsAnyOf,
      is: GetUserQualificationFilterOperator.Is,
      contains: GetUserQualificationFilterOperator.Contains,
      typeAreAnyOf: GetUserQualificationFilterOperator.TypeAreAnyOf,
    }

    const apiFilters: UserQualificationFilterModelDto[] = (filterModel?.items || [])
      .filter((item) => item.value && (Array.isArray(item.value) ? item.value.length > 0 : true))
      .map((item) => ({
        Field: fieldReverseMap[item.field],
        Operator: operatorReverseMap[item.operator],
        Value: filterValueFormatter(item),
      }))
      .filter((f: UserQualificationFilterModelDto) => f.Field !== undefined && f.Operator !== undefined)

    return apiFilters
  }, [filterModel])

  const pagination = usePagination({ total: totalCount, pageSize: 25 })

  const [qualificationToView, setQualificationToView] = useState<UserQualification | null>(null)

  const {
    loading,
    qualifications,
    totalCount: qualificationsTotalCount,
    onReload,
    createAsync,
    revokeAsync,
    restoreAsync,
  } = useUserQualifications({
    search: debouncedSearch,
    scope,
    sorting,
    filtering,
    pageNumber: pagination.current - 1,
    pageSize: pagination.size,
    userGuid: user?.IdentityGuid,
    userId: user?.Id,
    contractVersionId: contractVersion?.Id,
    displayType,
  })

  useEffect(() => {
    if (!loading) {
      setTotalCount(qualificationsTotalCount)
    }
  }, [qualificationsTotalCount, loading])

  const certificateTypeCustomFilterOperator = useMemo<GridFilterOperator<UserQualification>>(
    () => ({
      label: 'is any of',
      value: 'isAnyOf',
      getApplyFilterFn: () => {
        return () => true
      },
      InputComponent: CertificateTypeGridFilterOperator,
    }),
    []
  )

  const notifySuccessAndReload = useCallback(
    (message: string) => {
      dispatch(onAddSuccessNotification({ message }))
      onReload()
    },
    [onReload, dispatch]
  )

  const [addDialogOpen, onAddDialogOpen, onAddDialogClose] = useDialogState()
  const [viewDialogOpen, onViewDialogOpen, onViewDialogClose] = useDialogState()

  const onAddDialogSubmit = useCallback<AddUserQualificationDialogProps['onSubmit']>(
    async (values) => {
      onAddDialogClose()

      await createAsync(
        {
          AssignedToUserGuid: values.AssignedToUserGuid,
          AssignedToUserId: values.AssignedToUserId ?? 0,
          QualificationVersionId: Number(values.QualificationVersionId),
        },
        {
          onSuccess: () => {
            notifySuccessAndReload('User qualification was successfully created')
          },
          onError: (error) => {
            const message = error?.response?.data?.Error?.Message || 'Failed to create qualification'
            dispatch(onAddErrorNotification({ message }))
          },
        }
      )
    },
    [createAsync, onAddDialogClose, dispatch, notifySuccessAndReload]
  )

  const handleRestoreClick = useCallback(
    async (row: UserQualification) => {
      await restoreAsync(
        {
          userQualificationId: row.Id,
          userGuid: row.AssignedToUserId,
        },
        {
          onSuccess: () => notifySuccessAndReload('Qualification was successfully restored'),
          onError: () => dispatch(onAddErrorNotification({ message: 'Failed to restore qualification' })),
        }
      )
    },
    [dispatch, notifySuccessAndReload, restoreAsync]
  )

  const handleRevokeClick = useCallback(
    async (row: UserQualification) => {
      await revokeAsync(row.Id, {
        onSuccess: () => notifySuccessAndReload('Qualification was successfully revoked'),
        onError: () => dispatch(onAddErrorNotification({ message: 'Failed to revoke qualification' })),
      })
    },
    [dispatch, notifySuccessAndReload, revokeAsync]
  )

  const handleViewClick = useCallback(
    (row: UserQualification) => {
      setQualificationToView(row)
      onViewDialogOpen()
    },
    [onViewDialogOpen]
  )

  const columns = useMemo<GridColDef<UserQualification>[]>(
    () => [
      {
        field: 'AssignedToUserId',
        headerName: 'Assigned to',
        sortable: false,
        ...(user?.IdentityGuid && { filterable: false }),
        renderCell: ({ row: qualification }: GridRenderCellParams<UserQualification>) => (
          <UserInfo avatar={false} item={qualification.AssignedUser} />
        ),
        filterOperators: assignedToFilterOperator,
        flex: 1,
      },
      {
        field: 'AssignerId',
        headerName: 'Assigner',
        sortable: false,
        renderCell: ({ row: qualification }: GridRenderCellParams<UserQualification>) => (
          <UserInfo avatar={false} item={qualification.AssignerUser} />
        ),
        filterOperators: assignerFilterOperator,
        flex: 1,
      },
      {
        field: 'StatusId',
        headerName: 'Status',
        sortable: false,
        type: 'singleSelect',
        valueOptions: [
          UserAssignedQualificationStatusType.Pending,
          UserAssignedQualificationStatusType.Active,
          UserAssignedQualificationStatusType.Revoked,
          UserAssignedQualificationStatusType.CertificateExpired,
          UserAssignedQualificationStatusType.VersionExpired,
          UserAssignedQualificationStatusType.CertificateTypeDeleted,
          UserAssignedQualificationStatusType.QualificationDeleted,
        ].map((value) => ({
          value,
          label: UserAssignedQualificationStatusTypeDisplayName[value],
        })),
        width: 125,
        filterOperators: checkBoxFilterOperators,
        renderCell: ({ row }: GridRenderCellParams<UserQualification>) => (
          <UserQualificationStatusChip status={row.StatusId} />
        ),
      },
      {
        field: 'QualificationVersionName',
        headerName: 'Qualification version',
        sortable: false,
        filterable: false,
        flex: 1,
      },
      {
        field: 'QualificationVersionCertificateTypes',
        headerName: 'Certificate types',
        sortable: false,
        renderCell: ({ row }: GridRenderCellParams<UserQualification>) => {
          const limitTags = 1
          const numberOfTags = row.QualificationVersionCertificateTypes.length
          return (
            <Grid container direction='row' spacing={1} alignItems='center' wrap='nowrap'>
              {row.QualificationVersionCertificateTypes.slice(0, limitTags).map((el) => (
                <Grid item key={el.Id}>
                  <Chip color='default' size='small' label={el.Name} maxWidth={150} />
                </Grid>
              ))}
              {numberOfTags > limitTags && (
                <Grid item>
                  <span>{`+${numberOfTags - limitTags}`}</span>
                </Grid>
              )}
            </Grid>
          )
        },
        filterOperators: [certificateTypeCustomFilterOperator],
        flex: 1,
      },
      {
        field: 'QualificationVersionExpirationDate',
        headerName: 'Valid till',
        type: 'dateTime',
        valueGetter: ({ value }: GridValueGetterParams<UserQualification, string | null | undefined>) =>
          value && new Date(value),
        valueFormatter: ({ value }: GridValueFormatterParams<Date | null | undefined>) =>
          value ? value.toLocaleDateString('en-US') : 'Unlimited',
        filterable: false,
        width: 90,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 70,
        getActions: ({ row }: GridRowParams<UserQualification>) => [
          <GridActionsCellItem
            showInMenu
            icon={<AssignmentOutlined />}
            title='View details'
            label='View details'
            onClick={() => handleViewClick(row)}
          />,
          ...(!simpleView && row.StatusId === UserAssignedQualificationStatusType.Revoked
            ? [
                <GridActionsCellItem
                  showInMenu
                  icon={<Restore />}
                  title='Restore'
                  label='Restore'
                  onClick={() => handleRestoreClick(row)}
                />,
              ]
            : []),
          ...((!simpleView && row.StatusId === UserAssignedQualificationStatusType.Pending) ||
          row.StatusId === UserAssignedQualificationStatusType.Active
            ? [
                <GridActionsCellItem
                  showInMenu
                  icon={<Close />}
                  title='Revoke'
                  label='Revoke'
                  onClick={() => handleRevokeClick(row)}
                />,
              ]
            : []),
        ],
      },
    ],
    [
      user?.IdentityGuid,
      certificateTypeCustomFilterOperator,
      simpleView,
      handleViewClick,
      handleRestoreClick,
      handleRevokeClick,
    ]
  )

  const onRowDoubleClick = useCallback<GridEventListener<'rowDoubleClick'>>(
    ({ row: qualification }) => {
      handleViewClick(qualification)
    },
    [handleViewClick]
  )

  const columnVisibilityModel = useMemo<GridColumnVisibilityModel | undefined>(() => {
    if (!user?.IdentityGuid) {
      return undefined
    }

    return {
      AssignedToUserId: false,
    }
  }, [user?.IdentityGuid])

  const localeText = useMemo<Partial<GridLocaleText>>(
    () => ({
      noRowsLabel: 'No qualifications found',
      noResultsOverlayLabel: 'No qualifications found',
    }),
    []
  )

  return (
    <>
      {Toolbar && (
        <Toolbar
          filtersActive={filterModel && filterModel.items.length > 0}
          showScopeSelector={!personal}
          showAddButton={!simpleView}
          onOpenAddEditDialog={onAddDialogOpen}
          onReload={onReload}
          loading={loading}
          gridApiRef={gridApiRef}
          searchText={search}
          onSearchChange={setSearch}
          scope={scope}
          onScopeChange={setScope}
        />
      )}
      <DataGridV2
        apiRef={gridApiRef}
        loading={loading}
        rows={qualifications}
        columns={columns}
        rowCount={totalCount}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        paginationState={pagination}
        columnVisibilityModel={columnVisibilityModel}
        onRowDoubleClick={onRowDoubleClick}
        localeText={localeText}
      />
      <ViewUserQualificationDialog
        open={viewDialogOpen}
        qualification={qualificationToView}
        onClose={onViewDialogClose}
        onExited={() => setQualificationToView(null)}
      />
      <AddUserQualificationDialog open={addDialogOpen} onClose={onAddDialogClose} onSubmit={onAddDialogSubmit} />
    </>
  )
}

export default UserQualificationsGrid

